import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  GeoCalculationService,
  GeoCalculationServiceInjector,
  UpdateSize,
} from '@next/arable-lib/geo';
import { customRange } from '@next/arable-lib/shared';
import { CoreActions } from '@next/core-lib';
import { NavigationRailActions, PageSidesheetActions } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SessionActions, SessionSelectors } from '@next/core-lib/session';
import { SiteActions } from '@next/core-lib/site';
import { UserActions } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Feature, InteractionType } from 'src/app/geo/models';
import {
  DeleteFeature,
  DeselectCropFields,
  DeselectInteractionFeatures,
  FeatureActionTypes,
  InteractionFeatureDeselected,
  InteractionFeatureDrawn,
  InteractionFeatureModifying,
  InteractionFeatureSelected,
  SetLastYearsFeatures,
  UpsertFeature,
  UpsertFeatures,
} from 'src/app/geo/store/actions/feature.actions';

import {
  AppActionTypes,
  ContextChanged,
  GetCustomerData,
  GetDefaultCropYear,
  ImportCropYears,
  LoadAvailableTgaCropsSuccess,
  LoadCustomerProgress,
  LoadCustomerProgressSuccess,
  SelectCropFields,
  SetAvailableCropYears,
  SetCropFieldType,
  SetCropYear,
  SetDefaultCropYear,
  SetReportSort,
  ToggleCropFields,
} from './actions';
import {
  getAvailableCropYears,
  getCropFieldType,
  getCropYear,
  getDefaultCropYear,
  getReportSort,
  getUnselectedFeaturesIdsAsNumber,
  hasAllCropFieldsSelected,
  selectAvailableTgaCrops,
  selectCustomerProgress,
  selectSessionCustomerId,
} from './selectors';
import { SetInteractionType } from '../geo/store/actions/map-context.actions';
import {
  getModifyingFeature,
  getSelectedFeaturesIds,
} from '../geo/store/selectors/feature.selectors';
import { getInteractionType, isEditing } from '../geo/store/selectors/map-context.selectors';
import { FmsDiagnoseDialogComponent } from '../registration/components/fms-diagnose-dialog/fms-diagnose-dialog.component';
import { OverviewDialogComponent } from '../registration/components/overview-dialog/overview-dialog.component';
import {
  AddCropField,
  CropFieldActionTypes,
  DeleteCropFieldSuccess,
  EditCropFields,
  LoadCropFieldsRequest,
  LoadCropFieldsSuccess,
  LoadLastYearsCropFieldsRequest,
  LoadLastYearsCropFieldsSuccess,
  LoadMostUsedCropsRequest,
  PrefillCropField,
  UpsertCropFieldRequest,
  UpsertCropFieldSuccess,
} from '../registration/store/actions/crop-field.actions';
import {
  getCropField,
  getCropFields,
  getGroupedCropFields,
  getSelectedCropFields,
} from '../registration/store/selectors/crop-field.selectors';
import { ReportSort, ReportSortOption } from '../reports/models';
import { CropFieldType, SiteState } from '../shared/models';
import { ArableCustomerService } from '../shared/services/arable-customer.service';
import { PeriodService } from '../shared/services/arable-period.service';
import { TgaService } from '../shared/services/tga.service';
import * as SoilAnalysisActions from '../soil-analysis/store/soil-analysis.actions';

@Injectable()
export class AppEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly router = inject(Router);
  private readonly locale = inject(LocaleService);
  private readonly periodService = inject(PeriodService);
  private readonly arableCustomerService = inject(ArableCustomerService);
  private readonly tgaService = inject(TgaService);
  private readonly geoCalculation = inject<GeoCalculationService>(GeoCalculationServiceInjector);
  private readonly window = inject<WindowService>(WindowServiceInjector);
  private readonly platformID = inject<Object>(PLATFORM_ID);

  /**
   * userInitializedBefore is used to execute actions on the browser side during startup.
   */
  userInitializedBefore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitializedBefore),
      concatMap(() => [new GetDefaultCropYear(), SoilAnalysisActions.fetchDefaultSoilAnalyses()]),
    ),
  );
  /**
   * userInitialized+isPlatformBrowser does the same as above, but also works for newly
   * initialized sessions when users log in on this site.
   */
  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      filter(() => isPlatformBrowser(this.platformID)),
      concatMap(() => [new GetDefaultCropYear(), SoilAnalysisActions.fetchDefaultSoilAnalyses()]),
    ),
  );

  getDefaultCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetDefaultCropYear),
      switchMap(() =>
        this.periodService
          .getDefaultYear()
          .pipe(map((defaultYear: number) => new SetDefaultCropYear(defaultYear))),
      ),
    ),
  );

  setDefaultCropYear$ = createEffect(() => {
    const currentYear = new Date().getFullYear();
    const rangeStartYear = currentYear - 3;
    const rangeEndYear = currentYear + 1;
    const availableCropYears = customRange(rangeStartYear, rangeEndYear);
    const cropYearsList = this.setCropYearsToImport(currentYear);
    const result = this.actions$.pipe(
      ofType(AppActionTypes.SetDefaultCropYear),
      concatMap(() => [
        new SetAvailableCropYears(availableCropYears),
        new ImportCropYears(cropYearsList),
        new ContextChanged(),
      ]),
    );
    return result;
  });

  setCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropYear),
      map(() => new ContextChanged()),
    ),
  );

  setCropFieldType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropFieldType),
      map(() => new ContextChanged()),
    ),
  );

  customerInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerInitialized),
      filter(() => isPlatformBrowser(this.platformID)),
      map(() => new ContextChanged()),
    ),
  );

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      map(() => new ContextChanged()),
    ),
  );

  contextChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ContextChanged),
      withLatestFrom(
        this.store.pipe(select(selectSessionCustomerId)),
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(getCropFieldType)),
      ),
      distinctUntilChanged(
        (
          [_, customerId, cropYear, cropFieldType],
          [__, nextCustomerId, nextCropYear, nextCropFieldType],
        ) =>
          customerId === nextCustomerId &&
          cropYear === nextCropYear &&
          cropFieldType === nextCropFieldType,
      ),
      switchMap(([_, customerId, cropYear]) =>
        customerId && cropYear ? of(new GetCustomerData()) : EMPTY,
      ),
    ),
  );

  getCustomerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCustomerData),
      withLatestFrom(this.store.pipe(select(selectSessionCustomerId))),
      concatMap(([_, customerId]) => [
        new SetReportSort(this.loadReportSort(customerId)),
        new LoadCropFieldsRequest(true),
        new LoadLastYearsCropFieldsRequest(),
        new LoadMostUsedCropsRequest(),
        new LoadCustomerProgress(),
      ]),
    ),
  );

  resetCropFieldType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ResetCropFieldType),
      withLatestFrom(
        this.store.pipe(select(getCropFieldType)),
        this.store.pipe(select(SessionSelectors.selectSessionState)),
      ),
      switchMap(([{ cropFieldType }, currentCropFieldType, sessionState]) => {
        const arableSettings =
          sessionState && sessionState.arable && typeof sessionState.arable === 'object'
            ? sessionState?.arable
            : {};
        if (cropFieldType !== currentCropFieldType) {
          return of(
            SessionActions.setSession({
              data: {
                arable: {
                  ...arableSettings,
                  cropFieldType: cropFieldType === CropFieldType.Advice ? null : cropFieldType,
                },
              },
            }),
            new SetCropFieldType(),
          );
        } else {
          return of(new SetCropFieldType());
        }
      }),
    ),
  );

  resetCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ResetCropYear),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(getAvailableCropYears)),
        this.store.pipe(select(getDefaultCropYear)),
        this.store.pipe(select(SessionSelectors.selectSessionState)),
      ),
      switchMap(
        ([{ cropYear }, currentCropYear, availableCropYears, defaultCropYear, sessionState]) => {
          const arableSettings =
            sessionState && sessionState.arable && typeof sessionState.arable === 'object'
              ? sessionState.arable
              : {};
          let newCropYear: number = cropYear;
          if (isNaN(newCropYear) || availableCropYears.indexOf(newCropYear) === -1) {
            newCropYear = defaultCropYear;
          }
          if (newCropYear !== currentCropYear) {
            return of(
              SessionActions.setSession({
                data: {
                  arable: {
                    ...arableSettings,
                    cropYear: newCropYear === defaultCropYear ? null : newCropYear,
                  },
                },
              }),
              new SetCropYear(),
            );
          } else {
            return of(new SetCropYear());
          }
        },
      ),
    ),
  );

  upsertFeatures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadCropFieldsSuccess),
      mergeMap((action: LoadCropFieldsSuccess) => {
        const features = action.cropFields
          .map((cropField) =>
            cropField.feature
              ? {
                  ...cropField.feature,
                  id: cropField.id.toString(),
                }
              : null,
          )
          .filter((feature) => !!feature);

        return of(new UpsertFeatures(features));
      }),
    ),
  );

  setLastYearsFeatures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadLastYearsCropFieldsSuccess),
      mergeMap((action: LoadLastYearsCropFieldsSuccess) => {
        const features = action.cropFields
          .map((cropField) =>
            cropField.feature
              ? {
                  ...cropField.feature,
                  id: cropField.id.toString(),
                }
              : null,
          )
          .filter((feature) => !!feature);

        return of(new SetLastYearsFeatures(features));
      }),
    ),
  );

  upsertCropFieldSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.UpsertCropFieldSuccess),
      concatMap((action: UpsertCropFieldSuccess) =>
        action.created && action.cropField.feature
          ? [
              new UpsertFeature({
                ...action.cropField.feature,
                id: action.cropField.id.toString(),
              }),
            ]
          : EMPTY,
      ),
    ),
  );

  deleteCropFieldSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.DeleteCropFieldSuccess),
      map((action: DeleteCropFieldSuccess) => action.id),
      switchMap((id) => of(new DeleteFeature(id))),
    ),
  );

  featureDrawn$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.InteractionFeatureDrawn),
      concatMap((action: InteractionFeatureDrawn) => {
        this.store.dispatch(new PrefillCropField(true));
        return of(new AddCropField(action.feature));
      }),
    ),
  );

  interactionFeatureModified$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.InteractionFeatureModified),
      withLatestFrom(this.store.pipe(select(getModifyingFeature))),
      tap(() => this.store.dispatch(new InteractionFeatureModifying(null))),
      switchMap(([_, feature]: [never, Feature]) => {
        if (!feature) {
          return [new SetInteractionType(InteractionType.selectCropField)];
        }
        return this.store.pipe(
          select(getCropField({ id: feature.id })),
          take(1),
          concatMap((cropField) => [
            new UpsertCropFieldRequest({
              ...cropField,
              areaInHectares: this.geoCalculation.getAreaInHectares({
                ...feature,
              }),
              feature,
            }),
            new SetInteractionType(InteractionType.selectCropField),
          ]),
        );
      }),
    ),
  );

  selectAllCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SelectAllCropFields),
      withLatestFrom(this.store.pipe(select(getUnselectedFeaturesIdsAsNumber))),
      switchMap(([_, ids]) => ids.map((id) => new InteractionFeatureSelected(id))),
    ),
  );

  selectCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SelectCropFields),
      withLatestFrom(this.store.pipe(select(getSelectedFeaturesIds))),
      concatMap(([action, selectedFeatureIds]: [SelectCropFields, string[]]) => {
        if (
          action.cropFields.every(
            (cropField) => selectedFeatureIds.indexOf(cropField.id.toString()) > -1,
          )
        ) {
          return action.cropFields.map((cropField) => {
            if (!!cropField && !!cropField.id) {
              return new InteractionFeatureDeselected(cropField.id);
            }
          });
        }
        return action.cropFields
          .filter((cropField) => selectedFeatureIds.indexOf(cropField.id.toString()) === -1)
          .map((cropField) => {
            if (!!cropField && !!cropField.id) {
              return new InteractionFeatureSelected(cropField.id);
            }
          });
      }),
    ),
  );

  toggleCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleCropFields),
      withLatestFrom(this.store.pipe(select(hasAllCropFieldsSelected))),
      map(([action, allSelected]: [ToggleCropFields, boolean]) => {
        if (allSelected) {
          return new DeselectCropFields();
        }

        if (action.cropFields && action.cropFields.length > 0) {
          return new SelectCropFields(action.cropFields);
        } else {
          return new DeselectInteractionFeatures();
        }
      }),
    ),
  );

  updateSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PageSidesheetActions.sidesheetOpened,
        PageSidesheetActions.sidesheetClosed,
        WrapperSiteSidenavActions.collapseSidenav,
        WrapperSiteSidenavActions.openSidenav,
        NavigationRailActions.toggleNavigationRail,
      ),
      map((_) => new UpdateSize()),
    ),
  );

  allowSidenavOpen$ = createEffect(() =>
    this.store.pipe(
      select(isEditing),
      withLatestFrom(this.store.pipe(select(getInteractionType))),
      map(([editing, interactionType]) =>
        SiteActions.showFullscreen({
          showFullscreen:
            editing && interactionType !== InteractionType.linkSoilAnalysisToCropField,
        }),
      ),
    ),
  );

  openOverview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.OpenOverview),
        withLatestFrom(
          this.store.pipe(select(getGroupedCropFields)),
          this.store.pipe(select(getCropFields)),
        ),
        switchMap(([_, groupedCropFields, cropFields]) =>
          this.dialog
            .responsiveDialog(OverviewDialogComponent, {
              groupedCropFields,
              cropFields,
              fullscreen: true,
              altButton: true,
              altButtonText: this.locale.translate('printOverview'),
              altButtonIcon: 'print',
              altButtonColor: 'primary',
              pushHistoryState: false,
            })
            .pipe(
              switchMap(({ status }) => {
                if (status === DIALOG_RETURN_TYPES.alt) {
                  this.router.navigate(['cropfield-overview']);
                }
                return EMPTY;
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  viewSalesPotential$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.ViewSalesPotential),
        switchMap(() => {
          this.router.navigate(['sales-potential']);
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  openFmsDiagnose$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.OpenFmsDiagnose),
        switchMap(() =>
          this.dialog.responsiveDialog(
            FmsDiagnoseDialogComponent,
            {
              title: this.locale.translate('fmsDiagnose.title'),
              fullscreen: true,
              pushHistoryState: false,
            },
            {
              width: '100%',
              maxWidth: '90vw',
              height: '100vh',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  linkSoilAnalysisToCropField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.linkCropField),
      switchMap(({ soilAnalysisId }) =>
        this.actions$.pipe(
          ofType(FeatureActionTypes.InteractionFeatureSelected),
          take(1),
          withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
          switchMap(([_, cropFields]) =>
            of(
              new EditCropFields(
                [
                  {
                    ...cropFields[0],
                    soilAnalysisIds: [
                      ...cropFields[0].soilAnalysisIds.filter((id) => id !== soilAnalysisId),
                      soilAnalysisId,
                    ],
                  },
                ],
                {},
                { linkSoilAnalysisOrigin: true },
              ),
              new SetInteractionType(InteractionType.selectCropField),
              SoilAnalysisActions.deselectAllSoilAnalysisFeatures(),
            ),
          ),
          startWith(new SetInteractionType(InteractionType.linkSoilAnalysisToCropField)),
        ),
      ),
    ),
  );

  toggleReportSortOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleReportSortOption),
      withLatestFrom(this.store.pipe(select(getReportSort))),
      map(([_, reportSort]) => {
        const newReportSortOption =
          reportSort.option !== ReportSortOption.sortCropName
            ? ReportSortOption.sortCropName
            : ReportSortOption.sortCropFieldName;
        return new SetReportSort({
          option: newReportSortOption,
          descending: reportSort.descending,
        });
      }),
    ),
  );

  toggleReportSortDirection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleReportSortDirection),
      withLatestFrom(this.store.pipe(select(getReportSort))),
      map(
        ([_, reportSort]) =>
          new SetReportSort({
            option: reportSort.option,
            descending: !reportSort.descending,
          }),
      ),
    ),
  );

  setReportSort$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.SetReportSort),
        withLatestFrom(this.store.pipe(select(selectSessionCustomerId))),
        map(([{ reportSort }, customerId]) => this.saveReportSort(customerId, reportSort)),
      ),
    { dispatch: false },
  );

  loadCustomerProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCustomerProgress),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      mergeMap(([_, cropYear]) =>
        this.arableCustomerService.getCustomerProgress(cropYear).pipe(
          map((customerProgress) => new LoadCustomerProgressSuccess(customerProgress)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  toggleCustomerProgressAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleCustomerProgressAdvice),
      withLatestFrom(this.store.pipe(select(selectCustomerProgress))),
      mergeMap(([_, customerProgress]) =>
        this.arableCustomerService
          .upsertCustomerProgress({
            ...customerProgress,
            processes: {
              ...customerProgress.processes,
              fieldPlanCropFieldsCompleted:
                !customerProgress.processes.fieldPlanCropFieldsCompleted,
            },
          })
          .pipe(
            map(() => new LoadCustomerProgress()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  toggleCustomerProgressManure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleCustomerProgressManure),
      withLatestFrom(this.store.pipe(select(selectCustomerProgress))),
      mergeMap(([_, customerProgress]) =>
        this.arableCustomerService
          .upsertCustomerProgress({
            ...customerProgress,
            processes: {
              ...customerProgress.processes,
              fieldPlanManurespaceCompleted:
                !customerProgress.processes.fieldPlanManurespaceCompleted,
            },
          })
          .pipe(
            map(() => new LoadCustomerProgress()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  loadAvailableTgaCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadAvailableTgaCrops),
      withLatestFrom(this.store.pipe(select(selectAvailableTgaCrops))),
      mergeMap(([_, tgaCrops]) => {
        if (tgaCrops && tgaCrops.length > 0) {
          return EMPTY;
        }
        return this.tgaService.getAvailableTgaCropIds().pipe(
          map((cropIds) => new LoadAvailableTgaCropsSuccess(cropIds)),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError(() => EMPTY),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  private loadReportSort = (customerId: number): ReportSort => {
    const content = this.window.getCookie(this.getReportSortOptionCookieName(customerId));
    if (!content) {
      return { option: ReportSortOption.sortCropFieldName, descending: false };
    }

    const parts = content.split(' ');

    const option =
      ReportSortOption[parts[0] as keyof typeof ReportSortOption] ??
      ReportSortOption.sortCropFieldName;
    const descending = parts[1] === 'DESC';

    return { option, descending };
  };

  private saveReportSort(customerId: number, reportSort: ReportSort): void {
    if (reportSort?.option) {
      this.window.setCookie(
        this.getReportSortOptionCookieName(customerId),
        reportSort.option.toString() + (reportSort.descending ? ' DESC' : ''),
        365 * 24 * 60 * 60 * 1000,
      );
    }
  }

  private getReportSortOptionCookieName = (customerId: number): string =>
    `customer-${customerId}-report-sort-option}`;

  /** This function returns max 4 years.
   *  Beginning with the current year and going until 3 years back.
   *    E.g.: 2022 (current year), 2021, 2020, 2019
   *  It adds the next year if the current month is October.
   *    E.g.: 2023 (in October), 2022, 2021, 2020 **/
  setCropYearsToImport(currentYear: number): number[] {
    const cropYearsList: number[] = [];
    const startingYear = currentYear - 2;

    for (let i = currentYear - 2; i <= currentYear; i++) {
      cropYearsList.push(i);
    }
    const currentMonth = new Date().getMonth();
    if (currentMonth >= 9) {
      // Starting October, add the next crop year
      cropYearsList.push(currentYear + 1);
    } else {
      // Show 3 years back from the current year
      cropYearsList.push(startingYear - 1);
    }
    return cropYearsList.sort((a, b) => b - a);
  }
}
